/**
 * @module components/insights
 *
 * Thought leadership: articles, whitepapers, research and field notes, with
 * topic filters that update a live region and animate the grid.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on, setAttributes } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import { INSIGHTS, INSIGHTS_SUMMARY, getInsightTopics } from "../data/insights.js";
import { icon } from "../svg/icons.js";

/**
 * Render one insight card.
 *
 * @param {import("../data/insights.js").Insight} insight
 * @returns {string} Markup.
 */
function insightMarkup(insight) {
  const date = new Date(insight.date);
  const formatted = Number.isNaN(date.valueOf())
    ? insight.date
    : date.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });

  return `
    <li class="insight-card card${insight.featured ? " insight-card--featured" : ""}"
        data-insight="${insight.id}" data-topic="${insight.topic}"
        style="--insight-accent:${insight.accent}">
      <div class="insight-card__top">
        <span class="insight-card__type">${insight.type}</span>
        <span class="insight-card__reading">${insight.readingTime}</span>
      </div>
      <h3 class="insight-card__title">${insight.title}</h3>
      <p class="insight-card__abstract">${insight.abstract}</p>
      <div class="insight-card__foot">
        <span>${insight.topic} · <time datetime="${insight.date}">${formatted}</time></span>
        <a class="insight-card__more" href="${insight.href}"
           aria-label="Read: ${insight.title}">
          Read ${icon("arrowRight", { size: 14 })}
        </a>
      </div>
    </li>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initInsights() {
  const root = qs("[data-insights]");
  if (!root) return () => {};

  const eyebrow = qs("[data-insights-eyebrow]", root);
  const title = qs("[data-insights-title]", root);
  const lede = qs("[data-insights-lede]", root);
  if (eyebrow) eyebrow.textContent = INSIGHTS_SUMMARY.eyebrow;
  if (title) title.textContent = INSIGHTS_SUMMARY.title;
  if (lede) lede.textContent = INSIGHTS_SUMMARY.lede;

  const filterArea = qs("[data-insight-filters]", root);
  const grid = qs("[data-insight-grid]", root);
  const status = qs("[data-insight-status]", root);
  if (!grid) return () => {};

  grid.innerHTML = INSIGHTS.map(insightMarkup).join("");
  const cards = qsa("[data-insight]", grid);

  const topics = getInsightTopics();
  const filters = ["All", ...topics];

  if (filterArea) {
    filterArea.innerHTML = filters
      .map(
        (topic, index) => `
        <button type="button" class="btn btn--quiet" data-insight-filter="${topic}"
                aria-pressed="${index === 0}">${topic}</button>`
      )
      .join("");
  }

  const filterButtons = qsa("[data-insight-filter]", filterArea || root);
  const mm = createMotionContext();

  /**
   * Apply a topic filter.
   *
   * @param {string} topic
   */
  const apply = (topic) => {
    let visible = 0;

    cards.forEach((card) => {
      const matches = topic === "All" || card.getAttribute("data-topic") === topic;
      if (matches) visible += 1;
      setAttributes(card, { hidden: !matches });
    });

    filterButtons.forEach((button) =>
      setAttributes(button, { "aria-pressed": String(button.getAttribute("data-insight-filter") === topic) })
    );

    if (status) {
      status.textContent =
        topic === "All"
          ? `Showing all ${INSIGHTS.length} publications.`
          : `Showing ${visible} publication${visible === 1 ? "" : "s"} on ${topic}.`;
    }

    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const shown = cards.filter((card) => !card.hasAttribute("hidden"));
      gsap.fromTo(
        shown,
        { autoAlpha: 0, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.45, ease: EASE.out, stagger: 0.05, overwrite: true }
      );
    }
  };

  const disposers = filterButtons.map((button) =>
    on(button, "click", () => apply(button.getAttribute("data-insight-filter") ?? "All"))
  );

  apply("All");

  mm.add(MEDIA.any, () => {
    return watchReveal(cards, { from: { autoAlpha: 0, y: 22 }, duration: 0.6, stagger: 0.05, start: "top 82%" });
  });

  return () => {
    disposers.forEach((dispose) => dispose());
    mm.revert();
  };
}
